import { z } from 'zod'
import { SCHOOL_DEPARTMENTS, type SchoolDepartment } from './constants'
import { FlightLogInputSchema } from './flightLog'

// The flight school's contracts (school.* tables; folded in from
// jupi-fsm-app). Department-scoped access is by permission name -- see
// SCHOOL_PERMISSIONS in constants.ts.

export const SchoolDepartmentSchema = z.enum(SCHOOL_DEPARTMENTS)

export const LESSON_CLASSIFICATIONS = ['dual', 'solo', 'dual_xc', 'solo_xc'] as const
export type LessonClassification = (typeof LESSON_CLASSIFICATIONS)[number]

export const LESSON_GATES = ['pre_solo', 'pre_night_flying'] as const
export type LessonGate = (typeof LESSON_GATES)[number]

export const COMPLETE_WITHIN_UNITS = ['days', 'months', 'years'] as const
export type CompleteWithinUnit = (typeof COMPLETE_WITHIN_UNITS)[number]
export interface CompleteWithin {
  value: number
  unit: CompleteWithinUnit
}

// The date a course must be finished by when started on `startDate`
// (YYYY-MM-DD). Calendar arithmetic in UTC, so no timezone or DST shifts it;
// a month or year step that overshoots a short month lands on its last day
// (31 Aug + 6 months = 28/29 Feb).
export function targetFinishDate(startDate: string, within: CompleteWithin): string {
  const [y, m, d] = startDate.split('-').map(Number) as [number, number, number]
  if (within.unit === 'days') {
    return new Date(Date.UTC(y, m - 1, d + within.value)).toISOString().slice(0, 10)
  }
  const months = within.unit === 'years' ? within.value * 12 : within.value
  const first = new Date(Date.UTC(y, m - 1 + months, 1))
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  first.setUTCDate(Math.min(d, lastDay))
  return first.toISOString().slice(0, 10)
}

export interface Course {
  courseCode: string
  department: SchoolDepartment
  name: string
  supportsNfOption: boolean
  regulatoryMinimums: Record<string, unknown> | null
  // How long a student has to finish once started; null = no limit.
  completeWithin: CompleteWithin | null
  active: boolean
}

export interface SyllabusItem {
  itemCode: string
  description: string
}

export interface SyllabusLesson {
  lessonId: number
  lessonNumber: string
  title: string
  gateType: LessonGate | null
  defaultClassification: LessonClassification | null
  nominalHours: number | null
  notes: string | null
  briefingMaterialUrl: string | null
  items: SyllabusItem[]
}

export interface SyllabusModule {
  moduleNumber: number
  title: string
  lessons: SyllabusLesson[]
}

export interface CourseDetail extends Course {
  modules: SyllabusModule[]
}

// The instructor directory. Contact details and teaching credentials for
// every member; expiries and document names only for those who manage
// licences (see GET /api/school/instructors).
export interface DirectoryInstructor {
  userId: string
  displayName: string
  email: string | null
  phone: string | null
  departments: SchoolDepartment[]
  courses: string[]
  typeRatings: string[]
  // Instructor/examiner ratings held: FI, IRI, FE, FIE, COF.
  ratings: string[]
  // Set for an FI(A)*: an FI(A) working under a supervising FI(A) the
  // organisation has appointed.
  supervisor?: { userId: string; displayName: string } | null
  expiries?: Record<string, string | null>
  medicalClass?: string | null
  documents?: Record<string, string | null>
}

export const GrantCoursePrivilegeSchema = z.object({ instructorUserId: z.string().min(1) })
export const AppointSupervisorSchema = z.object({ supervisorUserId: z.string().min(1) })
export const GrantTypeRatingSchema = z.object({ aircraftType: z.string().trim().min(1).max(60) })

export const MANUAL_SCOPES = ['ato', 'dto', 'generic'] as const
export type ManualScope = (typeof MANUAL_SCOPES)[number]
export const ManualScopeSchema = z.enum(MANUAL_SCOPES)

export interface Manual {
  manualId: number
  scope: ManualScope
  title: string
  version: string | null
  revisionDate: string | null
  filename: string
  contentType: string
  uploadedAt: string
  uploadedByName: string | null
}

// --- Enrolments ------------------------------------------------------------

export const ENROLMENT_STATUSES = ['active', 'completed', 'withdrawn'] as const
export type EnrolmentStatus = (typeof ENROLMENT_STATUSES)[number]

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')

export const CreateEnrolmentSchema = z.object({
  studentUserId: z.string().min(1),
  courseCode: z.string().trim().toUpperCase().min(1),
  includesNf: z.boolean().default(false),
  startDate: IsoDate.nullable().optional(),
  targetFinishDate: IsoDate.nullable().optional(),
})
export type CreateEnrolmentInput = z.infer<typeof CreateEnrolmentSchema>

export const UpdateEnrolmentSchema = z
  .object({
    status: z.enum(ENROLMENT_STATUSES).optional(),
    startDate: IsoDate.nullable().optional(),
    targetFinishDate: IsoDate.nullable().optional(),
    finishDate: IsoDate.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })

export interface Enrolment {
  enrolmentId: number
  studentUserId: string
  studentName: string
  courseCode: string
  courseName: string
  department: SchoolDepartment
  status: EnrolmentStatus
  includesNf: boolean
  startDate: string | null
  targetFinishDate: string | null
  finishDate: string | null
  creditedHours: number
  createdAt: string
}

export const CreateCreditSchema = z.object({
  creditedHours: z.number().nonnegative().max(9999).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  lessonIds: z.array(z.number().int().positive()).max(500).default([]),
})
export type CreateCreditInput = z.infer<typeof CreateCreditSchema>

export interface EnrolmentCredit {
  creditId: number
  creditedHours: number | null
  notes: string | null
  documentFilename: string | null
  createdAt: string
  createdByName: string | null
  lessonIds: number[]
}

// --- Training flights, progress, pre-solo, certificates ----------------------

const ClockTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM')
  .nullable()
  .optional()

// A school flight is a normal flight (a flight.flight_logs row): `flight` is
// exactly what the flight log takes -- aircraft, flight type, date, the four
// times, airfields, landings, night/IFR, fuel, remarks. The API sets who is
// aboard from `classification`: dual, the logging instructor is the pilot and
// the student the crew; solo, the student is the pilot. So pilotUserId,
// pilotRole and the crew fields of `flight` are ignored. The rest is the
// school's own: the lesson, the checklist, the briefing.
export const CreateTrainingFlightSchema = z.object({
  // null: an extra flight, outside the syllabus order.
  lessonId: z.number().int().positive().nullable(),
  flight: FlightLogInputSchema,
  // Omitted: the lesson's default (dual for an extra flight).
  classification: z.enum(['dual', 'solo']).optional(),
  isCrossCountry: z.boolean().default(false),
  // Part of the block time; checked against the times by the API.
  instrumentMinutes: z.number().int().nonnegative().nullable().optional(),
  outsideAto: z.boolean().default(false),
  needsRepeat: z.boolean().default(false),
  briefingCompleted: z.boolean().default(false),
  briefingNotes: z.string().trim().max(4000).nullable().optional(),
  // Checklist items completed on this flight, from any lesson of the course.
  // The lesson booking this flight was flown on, if logged from one.
  bookingId: z.number().int().positive().nullable().optional(),
  // Further exercises flown together with `lessonId` (a combined exercise).
  combinedLessonIds: z.array(z.number().int().positive()).max(10).default([]),
  completedItems: z
    .array(z.object({ lessonId: z.number().int().positive(), itemCode: z.string().min(1) }))
    .max(500)
    .default([]),
})
export type CreateTrainingFlightInput = z.infer<typeof CreateTrainingFlightSchema>
export type CreateTrainingFlightDraft = z.input<typeof CreateTrainingFlightSchema>

export interface TrainingFlight {
  flightId: number
  enrolmentId: number
  lessonId: number | null
  lessonNumber: string | null
  lessonTitle: string | null
  instructorUserId: string | null
  instructorName: string | null
  registration: string | null
  flightDate: string
  offBlockTime: string | null
  takeoffTime: string | null
  landingTime: string | null
  onBlockTime: string | null
  durationMinutes: number
  departure: string | null
  destination: string | null
  // Landings and the flight log the flight is written in; null for a flight
  // logged before school flights went into the flight log.
  landings: number | null
  flightLogId: string | null
  classification: 'dual' | 'solo'
  isCrossCountry: boolean
  instrumentMinutes: number | null
  outsideAto: boolean
  needsRepeat: boolean
  briefingCompleted: boolean
  briefingNotes: string | null
  remarks: string | null
  items: { lessonId: number; itemCode: string }[]
  // The other exercises of a combined flight, beside lessonId.
  combinedLessons: { lessonId: number; lessonNumber: string }[]
  // Every checklist item of its exercise(s) is ticked; false shows yellow.
  fullyCompleted: boolean
  attachmentCount: number
  bookingId: number | null
}

export interface PreSoloCheck {
  theoryModule1Completed: boolean
  theoryConfirmedByName: string | null
  theoryConfirmedAt: string | null
  competenceAssessedByName: string | null
  competenceAssessedAt: string | null
  headTheoryOk: boolean
  headTheoryPercentage: number | null
  headTheoryConfirmedAt: string | null
}

export const PreSoloCheckSchema = z
  .object({ theoryModule1Completed: z.boolean().optional(), competenceAssessed: z.boolean().optional() })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })

export const HeadTheorySchema = z
  .object({
    headTheoryOk: z.boolean().optional(),
    headTheoryPercentage: z.number().int().min(0).max(100).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })

export type LessonStatus = 'not_started' | 'completed' | 'needs_repeat' | 'partial'

export interface EnrolmentProgress {
  enrolment: Enrolment
  modules: {
    moduleNumber: number
    title: string
    lessons: (Omit<SyllabusLesson, 'items'> & {
      status: LessonStatus
      // An instructor marked this ground exercise completed (no flight).
      groundCompleted: boolean
      items: (SyllabusItem & { completedVia: 'flight' | 'credit' | 'ground' | null })[]
    })[]
  }[]
  openItems: { lessonId: number; lessonNumber: string; lessonTitle: string; itemCode: string; description: string }[]
  preSoloCheck: PreSoloCheck | null
  soloFlown: boolean
  lastLessonNumber: string | null
  flownHours: number
  totalRequiredHours: number | null
  completionPercent: number | null
}

// The lesson a training flight is logged on by default: the first one not yet
// completed from the first lesson that is actually flown (PPL_A 4, LAPL_A 3b).
// The lessons before it (nominal 0 h) are ground exercises, not a flight.
export function nextFlightLesson<L extends { status: LessonStatus; nominalHours: number | null }>(lessons: L[]): L | null {
  const firstFlown = lessons.findIndex((l) => (l.nominalHours ?? 0) > 0)
  if (firstFlown < 0) return null
  // Later lessons with no flight time (a ground gate, a check) are not flown.
  return lessons.slice(firstFlown).find((l) => (l.nominalHours ?? 0) > 0 && l.status !== 'completed') ?? null
}

// A solo flight after the pre-solo lesson (14A) needs the student released
// for solo (theory module 1 ticked and competence assessed); the pre-solo lesson itself does not. An extra flight outside the
// syllabus counts as after it, and so does a flight whose combined exercises
// reach past it. `lessons` are the course's, in syllabus order. Courses with
// no pre-solo lesson have no gate.
export function preSoloGate(input: {
  lessons: { lessonId: number; gateType: LessonGate | null }[]
  lessonId: number | null
  combinedLessonIds?: number[]
  classification: 'dual' | 'solo'
  check: Pick<PreSoloCheck, 'theoryModule1Completed' | 'competenceAssessedAt'> | null
}): 'not_released' | null {
  if (input.classification !== 'solo') return null
  const gateAt = input.lessons.findIndex((l) => l.gateType === 'pre_solo')
  if (gateAt < 0) return null
  const position = (id: number | null) => (id === null ? Infinity : input.lessons.findIndex((l) => l.lessonId === id))
  const at = Math.max(position(input.lessonId), ...(input.combinedLessonIds ?? []).map(position))
  if (at <= gateAt) return null
  return input.check?.theoryModule1Completed && input.check.competenceAssessedAt ? null : 'not_released'
}

// Why an instructor cannot release a student for solo yet. The head of
// training must have ticked the theory as completed for solo and the
// student's medical must be valid; an FI(A)* may not release first solo.
export function soloReleaseRefusal(input: {
  headTheoryOk: boolean
  medicalValid: boolean
  releaserSupervised: boolean
}): 'supervised_instructor' | 'head_theory' | 'medical' | null {
  if (input.releaserSupervised) return 'supervised_instructor'
  if (!input.headTheoryOk) return 'head_theory'
  if (!input.medicalValid) return 'medical'
  return null
}

// A ground exercise: a lesson with no flight time before the first flown one
// (PPL_A 1-3, LAPL_A 1a-3a). It is done on the ground, so an instructor ticks
// it instead of logging a flight; a 0 h lesson later in the syllabus is not one.
export function isGroundExercise(lessons: { lessonId: number; nominalHours: number | null }[], lessonId: number): boolean {
  const at = lessons.findIndex((l) => l.lessonId === lessonId)
  const firstFlown = lessons.findIndex((l) => (l.nominalHours ?? 0) > 0)
  return at >= 0 && (lessons[at]!.nominalHours ?? 0) === 0 && (firstFlown < 0 || at < firstFlown)
}

export const REGULATION_REFERENCES = ['FI.ATO.2030', 'FI.DTO.0012'] as const

export const UpdateCertificateSchema = z
  .object({
    licenceNumber: z.string().trim().max(40).nullable().optional(),
    trainingStartDate: IsoDate.nullable().optional(),
    trainingFinishDate: IsoDate.nullable().optional(),
    regulationReference: z.enum(REGULATION_REFERENCES).optional(),
    recommendationText: z.string().trim().min(1).max(500).optional(),
    place: z.string().trim().max(100).nullable().optional(),
    issuedOn: IsoDate.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })

// --- Duty logs (ATO) ---------------------------------------------------------

export const CreateDutyLogSchema = z
  .object({
    logDate: IsoDate,
    dutyStart: ClockTime,
    dutyEnd: ClockTime,
    dutyMinutes: z.number().int().min(0).max(24 * 60).nullable().optional(),
    flightStart: ClockTime,
    flightEnd: ClockTime,
    flightMinutes: z.number().int().min(0).max(24 * 60).nullable().optional(),
    departure: z.string().trim().max(10).nullable().optional(),
    destination: z.string().trim().max(10).nullable().optional(),
    registration: z.string().trim().toUpperCase().max(10).nullable().optional(),
    remarks: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => v.dutyMinutes != null || (v.dutyStart && v.dutyEnd), {
    message: 'Give the duty times or its length',
    path: ['dutyMinutes'],
  })
export type CreateDutyLogInput = z.infer<typeof CreateDutyLogSchema>

export interface DutyLog {
  dutyLogId: number
  logDate: string
  dutyStart: string | null
  dutyEnd: string | null
  dutyMinutes: number | null
  flightStart: string | null
  flightEnd: string | null
  flightMinutes: number | null
  departure: string | null
  destination: string | null
  registration: string | null
  remarks: string | null
}

// From the day's ATO training flights (as instructor or student): duty
// padded an hour before the first off-block and half an hour after the
// last on-block, and the day's total block time.
export interface DutySuggestion {
  dutyStart: string
  dutyEnd: string
  blockMinutes: number
  flights: { flightId: number; offBlockTime: string | null; onBlockTime: string | null; durationMinutes: number; registration: string | null; courseName: string; withName: string | null }[]
}
